import {
  __,
  allPass,
  anyPass,
  complement,
  converge,
  curry,
  empty,
  equals,
  filter,
  find,
  findIndex,
  gte,
  head,
  ifElse,
  isEmpty,
  isNil,
  lensProp,
  lt,
  mergeRight,
  pipe,
  prop,
  propEq,
  view,
  pluck,
  uniq,
  flatten,
  includes,
  identity,
} from "ramda";
import * as steps from "./steps";
import * as applicationReducer from "application/applicationReducer";
import * as submissionReducer from "submission/submissionReducer";
import * as stepReducerCommon from "common/stepReducerCommon";
import * as optInReducerCommon from "common/optInReducerCommon";
import * as termsReducer from "terms/termsReducer";
import * as uploadReducer from "upload/uploadReducer";
import * as detailsReducer from "file-details/fileDetailsReducer";
import * as basicDetailsReducer from "basic-details/basicDetailsReducer";

export const getApplication = view(lensProp("application"));
const getSubmission = view(lensProp("submission"));
const getTerms = view(lensProp("terms"));
const getUpload = view(lensProp("upload"));
const getDetails = view(lensProp("details"));
const getPersonalInfo = view(lensProp("basicDetails"));
const getIntro = view(lensProp("intro"));
const getStep = curry((step, state) => view(lensProp(step), state));

export const isStepCompleted = curry((step, state) =>
  stepReducerCommon.getCompleted(getStep(step, state))
);

export const isStepActive = curry((step, state) =>
  pipe(getSubmission, submissionReducer.isActiveStep(step))(state)
);

export const getActiveStep = pipe(
  getSubmission,
  submissionReducer.getActiveStep
);

const getStepIndex = (step) => findIndex(equals(step), steps.list);

const isStepIndexComplete = (index, state) =>
  isStepCompleted(steps.list[index], state);

const isAllPreviousStepIndexesCompleted = (index, state) =>
  index < 1
    ? true
    : isStepIndexComplete(index - 1, state)
    ? isAllPreviousStepIndexesCompleted(index - 1, state)
    : false;

export const isAllPreviousStepsCompleted = curry((step, state) =>
  isAllPreviousStepIndexesCompleted(getStepIndex(step), state)
);

export const getIncompleteSteps = (state) =>
  filter(complement(isStepCompleted(__, state)), steps.list);

export const getNextIncompleteStep = pipe(getIncompleteSteps, head);

export const doesStepHaveErrors = curry((step, state) =>
  stepReducerCommon.hasErrors(getStep(step, state))
);

export const getProjectKey = pipe(
  getApplication,
  applicationReducer.getProjectKey
);

export const getProjectId = pipe(
  getApplication,
  applicationReducer.getProjectId
);

export const getUserLanguage = pipe(
  getApplication,
  applicationReducer.getLanguage
);

export const getProjectLogoUri = pipe(
  getApplication,
  applicationReducer.getProjectLogoUri
);

export const getProjectTermsUri = pipe(
  getApplication,
  applicationReducer.getProjectTermsUri
);

export const getProjectActive = pipe(
  getApplication,
  applicationReducer.getProjectActive()
);

export const getProjectHideCoverImageUpload = pipe(
  getApplication,
  applicationReducer.getProjectHideCoverImageUpload()
);

export const getProjectRedirectUri = pipe(
  getApplication,
  applicationReducer.getProjectRedirectUri
);

export const getDefaultLibraryId = pipe(
  getApplication,
  applicationReducer.getDefaultLibraryId
);

export const getProjectLibraries = pipe(
  getApplication,
  applicationReducer.getProjectLibraries
);

export const getOrganizationAlias = pipe(
  getApplication,
  applicationReducer.getOrganizationAlias
);

export const getOrganizationId = pipe(
  getApplication,
  applicationReducer.getOrganizationId
);

export const getAudiences = pipe(
  getApplication,
  applicationReducer.getAudiences
);

export const getSubProjects = pipe(
  getApplication,
  applicationReducer.getSubProjects
);

export const getCustomDropdownItems = pipe(
  getApplication,
  applicationReducer.getCustomDropdownItems
);

export const getFieldOptions = pipe(
  getApplication,
  applicationReducer.getFieldOptions
);

export const getCollectPurchaseLink = pipe(
  getApplication,
  applicationReducer.getCollectPurchaseLink
);

export const getCollectPublicationYear = pipe(
  getApplication,
  applicationReducer.getCollectPublicationYear
);

export const getProjectLanguages = pipe(
  getApplication,
  applicationReducer.getProjectLanguages
)

export const getContributorFields = pipe(
  getPersonalInfo,
  basicDetailsReducer.getContributorFields
);

export const getContributorErrors = pipe(
  getPersonalInfo,
  basicDetailsReducer.getContributorErrors
);

export const getAudienceName = pipe(
  getDetails,
  stepReducerCommon.getField(["audience", "name"])
);

export const getAudienceDisplay = pipe(
  getDetails,
  stepReducerCommon.getField(["audience", "display"])
);

const getStepProp = curry((lens, step, state) =>
  pipe(getStep(step), lens)(state)
);
export const getStepErrors = getStepProp(stepReducerCommon.getErrors);
export const getStepFields = getStepProp(stepReducerCommon.getFields);
export const getStepLoadingLocalStorage = getStepProp(
  stepReducerCommon.getLoadingLocalStorage
);

export const isTermsStepLoading = pipe(
  anyPass([getStepLoadingLocalStorage(steps.TERMS)])
);

export const getReviewOptIns = (state) =>
  pipe(
    getTerms,
    termsReducer.getTermsOptIns(
      getDetails(state),
      getPersonalInfo(state),
      getSubmission(state)
    )
  )(state);

export const getTermsOptIns = (state) =>
  pipe(
    getTerms,
    termsReducer.getTermsOptIns(
      getDetails(state),
      getPersonalInfo(state),
      getSubmission(state)
    )
  )(state);

export const getStepAcceptedOptIns = getStepProp(
  optInReducerCommon.getAcceptedOptIns
);

export const getStepDeclinedOptIns = getStepProp(
  optInReducerCommon.getDeclinedOptIns
);

const getStepOptInTermsDialog = getStepProp(
  optInReducerCommon.getOptInTermsDialog
);

const getOptInForId = (optInId, optIns) =>
  find(propEq("optInId", optInId), optIns);

export const getTermsOptInTermsDialog = curry((state) =>
  pipe(
    converge(
      (dialog, optIns) =>
        mergeRight(dialog, getOptInForId(dialog.optInId, optIns)),
      [getStepOptInTermsDialog(steps.TERMS), getTermsOptIns]
    )
  )
);

export const getOptInTermsDialog = converge(
  (dialog, optIns) => mergeRight(dialog, getOptInForId(dialog.optInId, optIns)),
  [getStepOptInTermsDialog(steps.TERMS), getTermsOptIns]
);

export const getTermsIframeLoading = pipe(
  getTerms,
  termsReducer.getIframeLoading
);

export const getCoverFile = pipe(getUpload, uploadReducer.getCoverFile);

export const getWorkFiles = pipe(getUpload, uploadReducer.getWorkFiles);

export const getWorkType = pipe(getSubmission, submissionReducer.getWorkType);
export const getWorkTypes = pipe(getSubmission, submissionReducer.getWorkTypes);
export const getSubmissionFile = pipe(
  getSubmission,
  submissionReducer.getSubmissionFile
);
export const getSubmissionFiles = pipe(
  getSubmission,
  submissionReducer.getSubmissionFiles
);

export const getCategories = pipe(
  getApplication,
  applicationReducer.getCategories
);

export const getVideoRatings = pipe(getDetails, detailsReducer.getVideoRatings);
export const isVideoRatingsEmpty = pipe(getVideoRatings, isEmpty);
export const isVideoWorkType = pipe(
  getWorkType,
  prop("contentTypeName"),
  equals("VIDEO")
);

export const getLicenseTypes = pipe(getDetails, detailsReducer.getLicenseTypes);

export const getAgeRanges = pipe(getDetails, detailsReducer.getAgeRanges);

export const isLicenseTypesEmpty = pipe(getLicenseTypes, isEmpty);

export const isAgeRangesEmpty = pipe(getAgeRanges, isEmpty);

export const getAllContributorRoles = pipe(
  getDetails,
  detailsReducer.getContributorRoles
);

export const getWorkTypeContributorRoles = pipe(
  getWorkType,
  prop("contributorRoles")
);

const checkRoles = ifElse(includes(undefined), empty, identity);

export const getSubmissionTypeContributorRoles = pipe(
  getApplication,
  applicationReducer.getSubmissionTypes,
  pluck("contributorRoles"),
  checkRoles,
  flatten,
  uniq
);

export const getContributorRoles = (state) =>
  getWorkTypeContributorRoles(state) || getAllContributorRoles(state);

export const getProjectContributorRoles = (state) => {
  const submissionRoles = getSubmissionTypeContributorRoles(state);
  if (submissionRoles && submissionRoles.length !== 0) {
    return submissionRoles;
  }

  return getAllContributorRoles(state);
};

export const isContributorRolesEmpty = pipe(getAllContributorRoles, isEmpty);

export const getContributors = pipe(getDetails, detailsReducer.getContributors);

export const isDetailsStepLoading = pipe(
  anyPass([
    getStepLoadingLocalStorage(steps.BASIC_DETAILS),
    isContributorRolesEmpty,
    allPass([isVideoWorkType, isVideoRatingsEmpty]),
  ])
);

export const getAddContributorDialogVisible = pipe(
  getDetails,
  allPass([
    detailsReducer.getContributorDialogVisible,
    pipe(detailsReducer.getContributorDialogIndex, lt(__, 0)),
  ])
);

export const getEditContributorDialogVisible = pipe(
  getDetails,
  allPass([
    detailsReducer.getContributorDialogVisible,
    pipe(detailsReducer.getContributorDialogIndex, gte(__, 0)),
  ])
);

export const getContributorDialogFields = pipe(
  getDetails,
  detailsReducer.getContributorDialogFields
);

export const getContributorDialogErrors = pipe(
  getDetails,
  detailsReducer.getContributorDialogErrors
);

export const getRemoveContributorDialogVisible = pipe(
  getDetails,
  detailsReducer.getRemoveContributorDialogVisible
);
export const getRemoveContributorDialogIndex = pipe(
  getDetails,
  detailsReducer.getRemoveContributorDialogIndex
);

export const getContributor = curry((index, state) =>
  pipe(getDetails, detailsReducer.getContributor(index))(state)
);

export const getRemoveTrackDialogVisible = pipe(
  getDetails,
  detailsReducer.getRemoveTrackDialogVisible
);
export const getRemoveTrackDialogTrack = pipe(
  getDetails,
  detailsReducer.getRemoveTrackDialogTrack
);

export const getPrimaryCategory = pipe(
  getStepFields(steps.FILE_DETAILS),
  prop("primaryCategory") //should be in reducer
);

export const getSecondaryCategory = pipe(
  getStepFields(steps.FILE_DETAILS),
  prop("secondaryCategory") //should be in reducer
);

export const getTertiaryCategory = pipe(
  getStepFields(steps.FILE_DETAILS),
  prop("tertiaryCategory") //should be in reducer
);

export const getCountries = pipe(
  getPersonalInfo,
  basicDetailsReducer.getCountries
);

export const isCountriesEmpty = pipe(getCountries, isEmpty);

export const getCountryCode = pipe(
  getPersonalInfo,
  stepReducerCommon.getField(["country", "code"])
);

export const getCountryName = pipe(
  getPersonalInfo,
  stepReducerCommon.getField(["country", "name"])
);

export const isCountryEmpty = pipe(getCountryCode, anyPass([isEmpty, isNil]));

export const getLibraries = pipe(
  getPersonalInfo,
  basicDetailsReducer.getLibraries
);

export const isLibrariesEmpty = pipe(getLibraries, isEmpty);

export const getSubdivisions = pipe(
  getPersonalInfo,
  basicDetailsReducer.getSubdivisions
);

export const getRegionCode = pipe(
  getPersonalInfo,
  stepReducerCommon.getField(["region", "code"])
);

export const getRegionName = pipe(
  getPersonalInfo,
  stepReducerCommon.getField(["region", "name"])
);

export const isPersonalInfoStepLoading = pipe(
  anyPass([
    getStepLoadingLocalStorage(steps.BASIC_DETAILS),
    isCountriesEmpty,
    isLibrariesEmpty,
  ])
);

export const getSubmitterIsCreator = pipe(
  getIntro,
  stepReducerCommon.getField(["isSubmitterCreator"])
);

export const getLocalLibraryOrganizationId = pipe(
  getPersonalInfo,
  stepReducerCommon.getField(["localLibrary", "organizationId"])
);

export const getLocalLibraryOrganizationName = pipe(
  getPersonalInfo,
  stepReducerCommon.getField(["localLibrary", "organizationName"])
);

export const getLocalLibraryOther = pipe(
  getPersonalInfo,
  stepReducerCommon.getField(["localLibraryOther"])
);

export const isLocalLibraryOtherEmpty = pipe(
  getLocalLibraryOther,
  anyPass([isEmpty, isNil])
);

export const getShowLocalLibraryOther = pipe(
  getPersonalInfo,
  basicDetailsReducer.getShowLocalLibraryOther
);

export const getInvalidSubmissionDialogVisible = pipe(
  getSubmission,
  submissionReducer.getInvalidSubmissionDialogVisible
);

export const getSubmissionErrorDialogVisible = pipe(
  getSubmission,
  submissionReducer.getSubmissionErrorDialogVisible
);

export const getValidationErrorsDialogVisible = pipe(
  getSubmission,
  submissionReducer.getValidationErrorsDialogVisible
);

export const getSubmissionPanelLoading = pipe(
  getSubmission,
  submissionReducer.getLoading
);

export const getCancelSubmissionDialogVisible = pipe(
  getSubmission,
  submissionReducer.getCancelSubmissionDialogVisible
);

export const getUploadDialogVisible = pipe(
  getSubmission,
  submissionReducer.getUploadDialogVisible
);

export const getUploadComplete = pipe(
  getSubmission,
  submissionReducer.getUploadComplete
);

export const getUploadProgress = pipe(
  getSubmission,
  submissionReducer.getUploadProgress
);

export const getUploadStep = pipe(
  getSubmission,
  submissionReducer.getUploadStep
);

export const isIntroStepLoading = pipe(
  anyPass([getStepLoadingLocalStorage(steps.INTRO)])
);
